import { BaseUser, BaseUserBuilder } from '@nab/nab-x-sdk-core';
import { UserTailoring } from './UserTailoring';
/**
 * [[include:packages/consumer/sdk-browser/src/User/README.md]]
 *
 * @todo Make constructor params an object - this will ensure no breaking change is required to add an additional param
 *
 * interface IUserParams {
 *     user: UserJson;
 *     app: IUserRelevantAppDetails;
 *     services: {
 *         analyticsMgr: AnalyticsMgr;
 *         dataSecurity: IDataSecurityMgr;
 *         flagMgr: ICoreFlagMgr;
 *         // ...future services
 *     }
 *     // ...future params
 * }
 *
 */
export class User extends BaseUser {
    constructor(user, app, analyticsMgr, dataSecurityInstance, baseFlagMgr) {
        super(user);
        this.user = user;
        this.app = app;
        this.analyticsMgr = analyticsMgr;
        this.dataSecurityInstance = dataSecurityInstance;
        this.baseFlagMgr = baseFlagMgr;
        this.tailoring = new UserTailoring(this.app?.appMetaData.id || 'SHELL', this, this.baseFlagMgr);
    }
    emitEvent(payload) {
        const payloadWithContextAndAppMetaData = {
            ...payload,
            data: {
                ...payload.data,
                ...this.context,
                ...(this.app && {
                    appMetaData: {
                        id: this.app.appMetaData.id,
                        name: this.app.appMetaData.name,
                        route: this.app.appMetaData.route,
                        description: this.app.appMetaData.description,
                        ...(this.app.appMetaData.parentContext && { parentContext: this.app.appMetaData.parentContext })
                    }
                })
            }
        };
        return this.analyticsMgr.logEvent(payloadWithContextAndAppMetaData);
    }
    /**
     * @deprecated This function will be deprecated in favour of the new function `user.dataSecurity` that will pass the object instance of shareable functions.
     */
    dataSecurityInfo(securityAlias) {
        return this.dataSecurityInstance[securityAlias].getData();
    }
    /**
     * Use this function to get the instance of the data security library with functions that can be used.
     * @param securityAlias The fraud library alias to retrieve the appropriate fraud library instance.
     * @returns Object of usable functions and values to be consumed from the library.
     * @throws library not initialised error if the security alias is incorrect or the shell has not initialised the library
     */
    dataSecurity(securityAlias) {
        const dataSecurityLib = this.dataSecurityInstance[securityAlias];
        if (dataSecurityLib) {
            // It was needed to bind the context of this to the functions extracted as it looses the other context in the new object
            const { getData, setContext } = dataSecurityLib;
            return {
                getData: getData.bind(dataSecurityLib),
                setContext: setContext.bind(dataSecurityLib)
            };
        }
        throw new Error(`Data security library ${securityAlias}, not initialised`);
    }
    static fromJSON(json, app, analyticsMgr, dataSecurity, baseFlagMgr) {
        return new User(json, app, analyticsMgr, dataSecurity, baseFlagMgr);
    }
    forkForApp(app) {
        return new User(this.user, app, this.analyticsMgr, this.dataSecurityInstance, this.baseFlagMgr);
    }
}
/**
 * [[include:packages/consumer/sdk-browser/src/User/UserBuilder.md]]
 */
export class UserBuilder extends BaseUserBuilder {
    constructor(user, appObj, analyticsMgrObj, dataSecurityObj, flagMgrObj) {
        super(user);
        this.appObj = appObj;
        this.analyticsMgrObj = analyticsMgrObj;
        this.dataSecurityObj = dataSecurityObj;
        this.flagMgrObj = flagMgrObj;
    }
    app(value) {
        this.appObj = value;
        return this;
    }
    analyticsManager(value) {
        this.analyticsMgrObj = value;
        return this;
    }
    dataSecurityManager(value) {
        this.dataSecurityObj = value;
        return this;
    }
    flagMgr(value) {
        this.flagMgrObj = value;
        return this;
    }
    build() {
        const { json, appObj, analyticsMgrObj, dataSecurityObj, flagMgrObj } = this;
        return Object.freeze(User.fromJSON(json, appObj, analyticsMgrObj, dataSecurityObj, flagMgrObj));
    }
}
//# sourceMappingURL=User.js.map